/**
 * API Client pour les mouvements de stock
 * Centralise les appels API liés à l'affichage des mouvements de stock
 * 
 * Note: Les fonctions d'audit out_shop sont uniquement côté backend
 * (startup et endpoints API directs, pas d'interface utilisateur)
 */

const API_BASE = '/api/stocks';

/**
 * Gère les erreurs d'API
 * @param {Response} response - Réponse fetch
 * @returns {Promise<any>}
 */
async function handleResponse(response) {
    // Vérifier le Content-Type avant de parser
    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');
    
    // Lire le body comme texte d'abord (on ne peut le lire qu'une fois)
    const text = await response.text();
    
    if (!isJson) {
        // Si ce n'est pas du JSON, c'est probablement une page d'erreur HTML
        const errorMsg = response.ok 
            ? `Réponse inattendue (${contentType || 'unknown'}): ${text.substring(0, 200)}`
            : `Erreur HTTP ${response.status}: ${text.substring(0, 200)}`;
        throw new Error(errorMsg);
    }
    
    // Parser le JSON
    let data;
    try {
        data = JSON.parse(text);
    } catch (parseError) {
        // Si le parsing JSON échoue malgré le Content-Type JSON
        throw new Error(`Erreur de parsing JSON: ${parseError.message}. Réponse: ${text.substring(0, 200)}`);
    }
    
    if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
}

/**
 * Récupère les mouvements enrichis pour un stock
 * @param {string} shopId - ID du shop
 * @param {string} productId - ID du produit
 * @returns {Promise<Object>} - { movements, current_stock, total_movements }
 */
export async function getStockMovements(shopId, productId) {
    const response = await fetch(
        `${API_BASE}/history/movements?shop_id=${encodeURIComponent(shopId)}&product_id=${encodeURIComponent(productId)}`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'same-origin',
        }
    );
    
    return handleResponse(response);
}

/**
 * Récupère l'historique de stock classique (ancien endpoint)
 * @param {string} stockID - Format "shop_id|product_id"
 * @returns {Promise<Array>}
 */
export async function getStockHistory(stockID) {
    const response = await fetch('/stocks/single', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            page: "stock/history",
            id: stockID
        }),
        credentials: 'same-origin',
    });
    
    return handleResponse(response);
}

export default {
    getStockMovements,
    getStockHistory
};

